import { Component, h } from 'preact';
import { Provider } from 'redux-zero/preact';
import { bindActions } from 'redux-zero/utils';
import { BookingWidget } from './BookingWidget/BookingWidget';
import { Environment, TravelType, WidgetTab, ContentType } from './common';
import { defaultExtensionApi, getConfig } from './config';
import { Actions, actions, BookingState, CompactWidgetState, initBookingStore, Store, StoreActions } from './store';
import { StyleProvider } from './style';
import {
  compareAsJson,
  DeepPartial,
  isFunction,
  isPresent,
  mergeArray,
  mergeDeep,
  MergeOptions,
  sanitize,
} from './utils';

import { getEnabledTabs } from './utils/utils';
import { DefaultProps, Props, OmitFirstArg } from './App.types';
import { preloadIcons } from './services';

const mergeOptions: MergeOptions = { overwriteWithEmpty: false, arrayMerge: mergeArray };

export type { Props } from './App.types';
export class App extends Component<DefaultProps> {
  static defaultProps: DefaultProps = {
    locale: 'en',
    env: Environment.PROD,
    embedded: false,
    markLocalizations: false,
    enablePersistence: true,
    renderMainHeading: false,
    enableAurinkomatkat: true,
    enableAward: false,
    enableAwardIfLoggedIn: false,
    enablePromoCode: false,
    enableCompactWidget: false,
    enableAutoFocus: true,
    enableOpenJaw: false,
    profile: {
      isLoggedIn: false,
    },
    activatedTab: WidgetTab.FLIGHT,
    isInitialRender: false,
    shouldExpandWidget: false,
    contentType: ContentType.FRONT_PAGE,
  };

  private readonly store: Store<BookingState>;
  private appActions: {
    setCurrentTab: OmitFirstArg<StoreActions<BookingState, Actions>['setCurrentTab'], Promise<void> | void>;
    mergePartialState: OmitFirstArg<StoreActions<BookingState, Actions>['mergePartialState'], Promise<void> | void>;
  };

  constructor({
    flow = {},
    locale = 'en',
    localizations,
    env = Environment.TEST,
    extensionApi,
    enablePersistence = true,
    enableAurinkomatkat,
    enableAward,
    enableAwardIfLoggedIn,
    enablePromoCode,
    enableCompactWidget,
    enableAutoFocus,
    enableOpenJaw,
    profile,
    testVariant,
    isInitialRender,
    shouldExpandWidget,
    contentType,
  }: Props) {
    super();

    preloadIcons();

    const stateOverrideParts: DeepPartial<BookingState>[] = [
      {
        selection: { locale },
        envConfig: getConfig(env),
        extensionApi: defaultExtensionApi,
        enableAurinkomatkat,
        enableAward,
        enableAwardIfLoggedIn,
        enablePromoCode,
        enableCompactWidget,
        enableAutoFocus,
        enableOpenJaw,
        profile,
        testVariant,
        isInitialRender,
        shouldExpandWidget,
        contentType,
      },
    ];
    stateOverrideParts.push({
      selection: {
        ...flow,
        locale: flow.locale || locale,
      },
    });
    if (isPresent(localizations)) {
      stateOverrideParts.push({
        ux: {
          i18n: {
            locale: flow.locale || locale,
            localizations,
          },
        },
      });
    }
    if (isPresent(extensionApi)) {
      stateOverrideParts.push({ extensionApi });
    }

    const stateOverrides: DeepPartial<BookingState> = sanitize(mergeDeep(stateOverrideParts, mergeOptions));
    this.store = initBookingStore({
      stateOverrides,
      enablePersistence,
      testVariant,
    });
    this.appActions = bindActions(actions, this.store);

    const { selection } = this.store.getState();

    if (!enablePromoCode) {
      this.store.setState({
        selection: {
          ...selection,
          promoCode: null,
        },
      });
    }

    if (
      selection.travelType === TravelType.MULTI_CITY ||
      (selection.locations[selection.activeLocationIndex].destination &&
        selection.locations[selection.activeLocationIndex].origin) ||
      shouldExpandWidget
    ) {
      const { ux } = this.store.getState();
      this.store.setState({
        ux: {
          ...ux,
          compactWidgetState: CompactWidgetState.OPENED,
        },
        enableAutoFocus,
      });
    }
  }

  componentWillReceiveProps(nextProps: DefaultProps): void {
    const {
      ux: { i18n },
    } = this.store.getState();

    const localizationsChanged =
      nextProps.localizations &&
      (!compareAsJson(nextProps.localizations, i18n.localizations) ||
        !compareAsJson(nextProps.localizations, this.props.localizations));
    const localeChanged = nextProps.locale !== this.props.locale;
    // we check only isLoggedIn property because the rest of the fields are not possible to
    // update without logging out and login afterwards.
    const profileChanged = nextProps.profile.isLoggedIn !== this.props.profile.isLoggedIn;

    if (localizationsChanged || localeChanged || profileChanged) {
      this.appActions.mergePartialState({
        ux: {
          i18n: {
            localizations: nextProps.localizations,
            locale: nextProps.locale,
          },
        },
        selection: {
          locale: nextProps.locale,
        },
        profile: nextProps.profile,
      });
    }

    if (nextProps.activatedTab !== this.props.activatedTab) {
      this.appActions.setCurrentTab(nextProps.activatedTab);
    }
  }

  componentDidMount(): void {
    if (this.props.activatedTab) {
      this.appActions.setCurrentTab(this.props.activatedTab);
    }
  }

  componentWillUnmount(): void {
    if (isFunction(this.store.shutdown)) {
      this.store.shutdown();
    }
  }

  render({
    embedded,
    campaignImage,
    renderMainHeading,
    markLocalizations,
    testVariant,
    enablePersonalization,
    enableAurinkomatkat,
    enableAward,
    enableAwardIfLoggedIn,
    enableCompactWidget,
    profile,
    locale,
  }: DefaultProps): h.JSX.Element {
    const enabledTabs = getEnabledTabs(locale, profile, enableAward, enableAwardIfLoggedIn, enableAurinkomatkat);
    return (
      <StyleProvider>
        <Provider store={this.store}>
          <BookingWidget
            embedded={embedded}
            campaignImage={campaignImage}
            renderMainHeading={renderMainHeading}
            testVariant={testVariant}
            markLocalizations={markLocalizations}
            enablePersonalization={enablePersonalization}
            enableCompactWidget={enableCompactWidget}
            enabledTabs={enabledTabs}
            profile={profile}
          />
        </Provider>
      </StyleProvider>
    );
  }
}
